import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from './LanguageContext';
function Counter({
  target,
  suffix
}: {
  target: number;
  suffix: string;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  useEffect(() => {
    if (isInView) {
      let start = 0;
      const duration = 2000;
      const step = 16;
      const increment = target / (duration / step);
      const timer = setInterval(() => {
        start += increment;
        if (start >= target) {
          setCount(target);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, step);
      return () => clearInterval(timer);
    }
  }, [isInView, target]);
  return <span ref={ref} className="font-serif text-[40px] lg:text-[56px] font-medium text-white block leading-none mb-4 tracking-[-0.02em]" data-id="element-264">
      {count}
      <span className="text-crimson-glow font-light" dangerouslySetInnerHTML={{
      __html: suffix
    }} data-id="element-265" />
    </span>;
}
export function Trust() {
  const {
    t
  } = useLanguage();
  return <section id="trust" className="relative py-10 lg:py-0 bg-[#020202] border-y border-white/[0.02] overflow-hidden trust-bg-gradient" data-id="element-266">
      <div className="max-w-[1400px] mx-auto px-8 lg:px-[60px] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-0 relative z-10" data-id="element-267">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 1.2,
        ease: [0.16, 1, 0.3, 1]
      }} className="py-10 lg:py-16 px-6 lg:px-12 text-center lg:border-r border-white/[0.03] relative transition-all duration-500 hover:bg-white/[0.01] group rounded-sm lg:rounded-none bg-white/[0.01] lg:bg-transparent border border-white/[0.03] lg:border-y-0 lg:border-l-0" data-id="element-268">
          <div className="absolute inset-0 bg-gradient-to-b from-crimson/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-269"></div>
          <Counter target={120} suffix="<span class='text-[32px] lg:text-[40px] ml-1'>M+</span>" data-id="element-270" />
          <span className="font-sans text-[10px] tracking-[4px] uppercase text-white/40 font-medium block mt-2" data-id="element-271">
            {t('trust.recovered')}
          </span>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 1.2,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.1
      }} className="py-10 lg:py-16 px-6 lg:px-12 text-center lg:border-r border-white/[0.03] relative transition-all duration-500 hover:bg-white/[0.01] group rounded-sm lg:rounded-none bg-white/[0.01] lg:bg-transparent border border-white/[0.03] lg:border-y-0 lg:border-l-0" data-id="element-272">
          <div className="absolute inset-0 bg-gradient-to-b from-crimson/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-273"></div>
          <Counter target={5000} suffix="<span class='text-[32px] lg:text-[40px] ml-1'>+</span>" data-id="element-274" />
          <span className="font-sans text-[10px] tracking-[4px] uppercase text-white/40 font-medium block mt-2" data-id="element-275">
            {t('trust.clients')}
          </span>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 1.2,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.2
      }} className="py-10 lg:py-16 px-6 lg:px-12 text-center lg:border-r border-white/[0.03] relative transition-all duration-500 hover:bg-white/[0.01] group rounded-sm lg:rounded-none bg-white/[0.01] lg:bg-transparent border border-white/[0.03] lg:border-y-0 lg:border-l-0" data-id="element-276">
          <div className="absolute inset-0 bg-gradient-to-b from-crimson/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-277"></div>
          <Counter target={24} suffix="<span class='text-[32px] lg:text-[40px] ml-1'>/7</span>" data-id="element-278" />
          <span className="font-sans text-[10px] tracking-[4px] uppercase text-white/40 font-medium block mt-2" data-id="element-279">
            {t('trust.available')}
          </span>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 1.2,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.3
      }} className="py-10 lg:py-16 px-6 lg:px-12 text-center relative transition-all duration-500 hover:bg-white/[0.01] group rounded-sm lg:rounded-none bg-white/[0.01] lg:bg-transparent border border-white/[0.03] lg:border-0" data-id="element-280">
          <div className="absolute inset-0 bg-gradient-to-b from-crimson/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-281"></div>
          <Counter target={0} suffix="<span class='text-[32px] lg:text-[40px] ml-1'>%</span>" data-id="element-282" />
          <span className="font-sans text-[10px] tracking-[4px] uppercase text-white/40 font-medium block mt-2" data-id="element-283">
            {t('trust.fee')}
          </span>
        </motion.div>
      </div>
    </section>;
}